import { extraStepSelectsEmployee } from '@/utils/employees/scheduleEmployees'
import { getOrgBusinessType } from '@/api/org'
import { getOrgServices } from '@/api/service.js'
import { getOrgCategories } from '@/api/service_category.js'
import { getFormConfigByOrg } from '@/api/custom_form.js'
import { getOrgCalendarRecordMarkers } from '@/api/record.js'
import { getWorkplaceForm, WORKPLACE_TERM_KEYS } from '@/utils/workplace'
import { sortRecordStages } from '@/utils/recordStages'
import {
  buildCalendarRecordMarkersCacheKey,
  getCalendarMonthDateRange,
} from '@/utils/calendarRecordMarkers'

/** In-flight загрузки каталога услуг по orgId (не в state — не реактивно). */
const orgCatalogLoadPromises = {}

/** In-flight загрузки client-form-config по orgId. */
const clientFormConfigLoadPromises = {}

/** In-flight загрузки маркеров мини-календаря по cacheKey. */
const calendarRecordMarkersLoadPromises = {}

/** loadOrganizationData принимает orgId или { orgId, force }. */
function resolveOrgLoadPayload(payload) {
  if (payload != null && typeof payload === 'object') {
    return {
      orgId: payload.orgId,
      force: payload.force === true,
    }
  }
  return { orgId: payload, force: false }
}

export const state = () => ({
  // Информация об организации
  organization: null,

  // Тип бизнеса и подтип
  businessType: null,
  businessSubtype: null,

  // Настройки отображения для универсального ЛК
  isUniversalBusiness: false,
  workplaceTitle: WORKPLACE_TERM_KEYS.PLACE, // ключ перевода термина или название пользователя
  activityField: '', // Сфера деятельности

  // Флаги для отображения/скрытия элементов
  showCarFields: true, // Показывать поля автомобилей
  showCarTypeFields: true, // Показывать поля типов кузова

  // Данные календаря для переиспользования в сайд-панели: список филиалов + посты по филиалам
  calendarDepartmentsByOrg: {}, // { [orgId]: { list: [], postsByDept: { [departmentId]: posts } } }

  // Справочник статусов записи (stage) — заполняется календарём, сайд-панель читает из стора
  recordStages: [],

  // Записи организации по неделям (calendar-records): ключ weekKey = from (YYYY-MM-DD), сайд-панель переиспользует
  calendarRecordsByOrgAndWeek: {}, // { [orgId]: { [weekKey]: records } }

  // Маркеры дней с записями для мини-календарей: ключ org|dept|post|tz|YYYY-MM
  calendarRecordMarkersByKey: {}, // { [cacheKey]: Array<{ date, count }> }

  // Каталог услуг/категорий по orgId — сайд-панель не дергает API на каждое открытие
  orgCatalogByOrg: {}, // { [orgId]: { categories: [], services: [] } }

  // Конфиг кастомной формы клиента по orgId (client-form-config): список и модалка переиспользуют
  clientFormConfigByOrg: {}, // { [orgId]: { fields, ... } }

  // Значения кастомной формы по парам clientId_carId: модалка редактирования кеширует свои getFormValues
  clientCarValuesCache: {}, // { "clientId_carId": { clientValues, carValues } }
})

export const mutations = {
  setOrganization(state, organization) {
    state.organization = organization
  },

  setBusinessType(state, businessType) {
    state.businessType = businessType
  },

  setBusinessSubtype(state, businessSubtype) {
    state.businessSubtype = businessSubtype
  },

  setUniversalBusiness(state, isUniversal) {
    state.isUniversalBusiness = isUniversal
  },

  setWorkplaceTitle(state, title) {
    state.workplaceTitle = title
  },

  setActivityField(state, field) {
    state.activityField = field
  },

  setShowCarFields(state, show) {
    state.showCarFields = show
  },

  setShowCarTypeFields(state, show) {
    state.showCarTypeFields = show
  },

  setCalendarDepartmentsList(state, { orgId, list }) {
    if (!state.calendarDepartmentsByOrg[orgId]) {
      state.calendarDepartmentsByOrg[orgId] = { list: [], postsByDept: {} }
    }
    state.calendarDepartmentsByOrg[orgId].list = list || []
  },

  setCalendarDepartmentPosts(state, { orgId, departmentId, posts }) {
    if (!state.calendarDepartmentsByOrg[orgId]) {
      state.calendarDepartmentsByOrg[orgId] = { list: [], postsByDept: {} }
    }
    if (!departmentId) return
    state.calendarDepartmentsByOrg[orgId].postsByDept[departmentId] =
      posts || []
  },

  /** Справочник статусов записи (stage), заполняется календарём */
  setRecordStages(state, list) {
    state.recordStages = list || []
  },

  /** Записи организации за неделю (calendar-records), заполняется календарём/сайд-панелью */
  setCalendarWeekRecords(state, { orgId, weekKey, records }) {
    const id = String(orgId)
    if (!state.calendarRecordsByOrgAndWeek[id]) {
      state.calendarRecordsByOrgAndWeek[id] = {}
    }
    state.calendarRecordsByOrgAndWeek[id][weekKey] = records || []
  },

  setCalendarRecordMarkers(state, { cacheKey, markers }) {
    if (!cacheKey) return
    state.calendarRecordMarkersByKey = {
      ...state.calendarRecordMarkersByKey,
      [cacheKey]: Array.isArray(markers) ? markers : [],
    }
  },

  clearCalendarRecordMarkers(state, orgId = null) {
    if (orgId == null) {
      state.calendarRecordMarkersByKey = {}
      return
    }
    const prefix = `${String(orgId)}|`
    const next = {}
    Object.keys(state.calendarRecordMarkersByKey || {}).forEach((key) => {
      if (!key.startsWith(prefix)) {
        next[key] = state.calendarRecordMarkersByKey[key]
      }
    })
    state.calendarRecordMarkersByKey = next
  },

  /** Каталог услуг/категорий организации для сайд-панели */
  setOrgCatalog(state, { orgId, categories, services }) {
    if (orgId == null) return
    state.orgCatalogByOrg[orgId] = {
      categories: Array.isArray(categories) ? categories : [],
      services: Array.isArray(services) ? services : [],
    }
  },

  /** Сброс каталога (после изменения услуг в настройках). */
  clearOrgCatalog(state, orgId) {
    if (orgId == null) {
      state.orgCatalogByOrg = {}
      return
    }
    const next = { ...state.orgCatalogByOrg }
    delete next[orgId]
    state.orgCatalogByOrg = next
  },

  /** Конфиг кастомной формы клиента (client-form-config), переиспользуется списком и модалкой */
  setClientFormConfig(state, { orgId, config }) {
    if (orgId == null) return
    state.clientFormConfigByOrg[String(orgId)] = config ?? null
  },

  /** Добавить одну пару в кеш (после getFormValues в модалке) */
  setClientCarValuesOne(state, { key, data }) {
    if (!key) return
    state.clientCarValuesCache = { ...state.clientCarValuesCache, [key]: data }
  },

  /** Очистить кеш значений формы для всех пар конкретного клиента */
  clearClientCarValuesByClientId(state, clientId) {
    if (!clientId) return
    const clientPrefix = `${clientId}_`
    const nextCache = {}

    Object.entries(state.clientCarValuesCache).forEach(([key, value]) => {
      if (!key.startsWith(clientPrefix)) {
        nextCache[key] = value
      }
    })

    state.clientCarValuesCache = nextCache
  },
}

export const actions = {
  // Загрузка данных организации для типа бизнеса/ЛК (без departments, employees и т.д.)
  async loadOrganizationData({ state, commit }, payload) {
    const { orgId, force } = resolveOrgLoadPayload(payload)
    const id = Number(orgId)
    if (!force && state.organization?.id === id) {
      return state.organization
    }
    const response = await getOrgBusinessType(orgId)
    const organization = response.data

    commit('setOrganization', organization)

    // Определяем тип бизнеса
    if (
      organization.business_subtypes &&
      organization.business_subtypes.length > 0
    ) {
      const businessSubtype = organization.business_subtypes[0]
      const businessType = businessSubtype.business_type

      commit('setBusinessSubtype', businessSubtype)
      commit('setBusinessType', businessType)

      // Проверяем, является ли это универсальным бизнесом
      const isUniversal = Boolean(businessSubtype.isUniversal)
      commit('setUniversalBusiness', isUniversal)

      // Устанавливаем настройки отображения
      if (isUniversal) {
        // Для универсального ЛК
        commit(
          'setWorkplaceTitle',
          organization.service_place || WORKPLACE_TERM_KEYS.PLACE
        )
        commit('setActivityField', organization.activity_field || '')
        commit('setShowCarFields', false)
        commit('setShowCarTypeFields', false)
      } else {
        // Для автомобильного бизнеса
        commit('setWorkplaceTitle', WORKPLACE_TERM_KEYS.POST)
        commit('setShowCarFields', true)
        commit('setShowCarTypeFields', true)
      }
    }

    return organization
  },

  // Принудительно перечитывает org (флаги онлайн-записи, график).
  async refreshOrganizationData({ dispatch }, orgId) {
    await dispatch('loadOrganizationData', { orgId, force: true })
  },

  /**
   * Каталог categories + services для сайд-панели (один раз на org за сессию).
   * Параллельные открытия панели делят один in-flight promise.
   */
  loadOrgCatalogIfNeeded({ state, commit }, orgId) {
    const id = String(orgId)
    const cached = state.orgCatalogByOrg[id]
    if (cached?.categories && cached?.services) {
      return Promise.resolve(cached)
    }

    if (!orgCatalogLoadPromises[id]) {
      orgCatalogLoadPromises[id] = Promise.all([
        getOrgCategories(orgId),
        getOrgServices(orgId),
      ])
        .then(([categoriesRes, servicesRes]) => {
          const payload = {
            categories: categoriesRes?.data ?? [],
            services: servicesRes?.data ?? [],
          }
          commit('setOrgCatalog', { orgId: id, ...payload })
          return payload
        })
        .finally(() => {
          delete orgCatalogLoadPromises[id]
        })
    }

    return orgCatalogLoadPromises[id]
  },

  /**
   * Конфиг кастомной формы клиента (один раз на org).
   * Календарь может прогреть до открытия сайд-панели.
   */
  loadClientFormConfigIfNeeded({ state, commit }, orgId) {
    const id = String(orgId)
    const cached = state.clientFormConfigByOrg[id]
    if (cached?.fields) {
      return Promise.resolve(cached)
    }

    if (!clientFormConfigLoadPromises[id]) {
      clientFormConfigLoadPromises[id] = getFormConfigByOrg(orgId)
        .then((response) => {
          const configData = response?.data?.data ?? null
          commit('setClientFormConfig', { orgId: id, config: configData })
          return configData
        })
        .finally(() => {
          delete clientFormConfigLoadPromises[id]
        })
    }

    return clientFormConfigLoadPromises[id]
  },

  /**
   * Маркеры дней с записями на месяц (мини-календари).
   * Кэш по org|dept|post|tz|YYYY-MM; force — перезапрос.
   */
  loadCalendarRecordMarkersIfNeeded(
    { state, commit },
    {
      orgId,
      monthKey,
      timezone,
      departmentId = null,
      postId = null,
      force = false,
    }
  ) {
    if (orgId == null || !monthKey) {
      return Promise.resolve([])
    }

    const cacheKey = buildCalendarRecordMarkersCacheKey({
      orgId,
      departmentId,
      postId,
      timezone,
      monthKey,
    })

    if (!force && state.calendarRecordMarkersByKey[cacheKey]) {
      return Promise.resolve(state.calendarRecordMarkersByKey[cacheKey])
    }

    if (!force && calendarRecordMarkersLoadPromises[cacheKey]) {
      return calendarRecordMarkersLoadPromises[cacheKey]
    }

    const { from, to } = getCalendarMonthDateRange(monthKey, timezone)

    calendarRecordMarkersLoadPromises[cacheKey] = getOrgCalendarRecordMarkers(
      orgId,
      {
        from,
        to,
        timezone,
        departmentId,
        postId,
      }
    )
      .then((res) => {
        const markers = Array.isArray(res?.data) ? res.data : []
        commit('setCalendarRecordMarkers', { cacheKey, markers })
        return markers
      })
      .finally(() => {
        delete calendarRecordMarkersLoadPromises[cacheKey]
      })

    return calendarRecordMarkersLoadPromises[cacheKey]
  },
}

export const getters = {
  /** Список филиалов (depts-list), для сайд-панели и др. */
  getCalendarDepartmentsList: (state) => (orgId) =>
    state.calendarDepartmentsByOrg[orgId]?.list ?? [],

  /** Посты филиала из календаря (calendar-depts-posts), для сайд-панели */
  getCalendarDepartmentPosts: (state) => (orgId, departmentId) =>
    state.calendarDepartmentsByOrg[orgId]?.postsByDept?.[departmentId] ?? null,

  /** Справочник статусов записи (stage), заполняется календарём, сайд-панель читает */
  getRecordStages: (state) => state.recordStages,

  /** Справочник статусов записи в порядке отображения в UI. */
  getRecordStagesSorted: (state) => sortRecordStages(state.recordStages || []),

  /** Записи организации за неделю (weekKey = from YYYY-MM-DD), для сайд-панели */
  getCalendarWeekRecords: (state) => (orgId, weekKey) =>
    state.calendarRecordsByOrgAndWeek[String(orgId)]?.[weekKey] ?? null,

  /** Маркеры дней мини-календаря по cacheKey */
  getCalendarRecordMarkers: (state) => (cacheKey) =>
    state.calendarRecordMarkersByKey[cacheKey] ?? null,

  /** Каталог услуг/категорий организации (сайд-панель) */
  getOrgCatalog: (state) => (orgId) =>
    state.orgCatalogByOrg[String(orgId)] ?? null,

  /** Конфиг кастомной формы клиента (client-form-config), для страницы клиентов и модалки */
  getClientFormConfig: (state) => (orgId) =>
    state.clientFormConfigByOrg[String(orgId)] ?? null,

  /** Значения кастомной формы для пары clientId_carId (модалка без values?clientId&carId) */
  getClientCarValues: (state) => (clientId, carId) =>
    state.clientCarValuesCache[`${clientId}_${carId}`] ?? null,

  /**
   * Базовое название поста/рабочего места
   * @param {Object} state - состояние store
   * @returns {string} "Пост" или "Рабочее место"
   * @example
   * // В компоненте
   * this.$store.getters['business/getPostName'] // => "Пост"
   */
  getPostName: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'nominative')
  },

  /**
   * Название поста с заглавной буквы (именительный падеж: кто? что?)
   * @param {Object} state - состояние store
   * @returns {string} "Пост" или "Рабочее место"
   * @example
   * // Использование: "Пост №1"
   * `${getPostNameCapitalized} №1`
   */
  getPostNameCapitalized: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'nominative')
  },

  /**
   * Название поста в контексте с заглавной (родительный падеж: кого? чего?)
   * @param {Object} state - состояние store
   * @returns {string} "Поста" или "Рабочего места"
   * @example
   * // Использование: "Название Поста"
   * `Название ${getPostNameContext}`
   */
  getPostNameContext: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'genitive')
  },

  /**
   * Название поста в контексте строчными (родительный падеж: кого? чего?)
   * @param {Object} state - состояние store
   * @returns {string} "поста" или "рабочего места"
   * @example
   * // Использование: "название поста"
   * `название ${getPostNameContextLower}`
   */
  getPostNameContextLower: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'genitive', {
      capitalize: false,
    })
  },

  /**
   * Название поста в дательном падеже (кому? чему?)
   * @param {Object} state - состояние store
   * @returns {string} "Посту" или "Рабочему месту"
   * @example
   * // Использование: "Присвоить Посту"
   * `Присвоить ${getPostNameDative}`
   */
  getPostNameDative: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'dative')
  },

  /**
   * Название поста для действия с заглавной (винительный падеж: кого? что?)
   * @param {Object} state - состояние store
   * @returns {string} "Пост" или "Рабочее место"
   * @example
   * // Использование: "Выбрать Пост"
   * `Выбрать ${getPostNameAction}`
   */
  getPostNameAction: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'accusative')
  },

  /**
   * Название поста для действия строчными (винительный падеж: кого? что?)
   * @param {Object} state - состояние store
   * @returns {string} "пост" или "рабочее место"
   * @example
   * // Использование: "выбрать пост", "добавить пост"
   * `Добавить ${getPostNameActionLower}`
   */
  getPostNameActionLower: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'accusative', {
      capitalize: false,
    })
  },

  /**
   * Название поста в творительном падеже (кем? чем?)
   * @param {Object} state - состояние store
   * @returns {string} "Постом" или "Рабочим местом"
   * @example
   * // Использование: "Управлять Постом"
   * `Управлять ${getPostNameInstrumental}`
   */
  getPostNameInstrumental: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'instrumental')
  },

  /**
   * Название поста в предложном падеже (о ком? о чем?)
   * @param {Object} state - состояние store
   * @returns {string} "Посте" или "Рабочем месте"
   * @example
   * // Использование: "О Посте"
   * `О ${getPostNamePrepositional}`
   */
  getPostNamePrepositional: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'prepositional')
  },

  /**
   * Название поста во множественном числе
   * @param {Object} state - состояние store
   * @returns {string} "Посты" или "Рабочие места"
   * @example
   * // Использование: "Посты филиала"
   * `${getPostNamePlural} филиала`
   */
  getPostNamePlural: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'plural')
  },

  /**
   * Название поста во множественном числе (родительный падеж)
   * @param {Object} state - состояние store
   * @returns {string} "Постов" или "Рабочих мест"
   * @example
   * // Использование: "Выбрано постов: 2"
   * `Выбрано ${getPostNamePluralGenitive}: 2`
   */
  getPostNamePluralGenitive: (state) => {
    return getWorkplaceForm(state.workplaceTitle, 'pluralGenitive')
  },

  /**
   * Все формы термина для i18n-шаблонов с падежами (монгольские строки).
   * @param {Object} state - состояние store
   * @returns {Object<string, string>} post/posts и формы в падежах
   */
  getPostNameForms: (state) => {
    const value = state.workplaceTitle
    const form = (formName) => getWorkplaceForm(value, formName)

    return {
      post: form('nominative'),
      postGen: form('genitive'),
      postDat: form('dative'),
      postAcc: form('accusative'),
      postInstr: form('instrumental'),
      posts: form('plural'),
      postsGen: form('pluralGenitive'),
      postsDat: form('pluralDative'),
      postsAcc: form('pluralAccusative'),
      postsAbl: form('pluralAblative'),
      postsInstr: form('pluralInstrumental'),
    }
  },

  /**
   * Проверить, является ли бизнес универсальным
   * @param {Object} state - состояние store
   * @returns {boolean} true для универсального бизнеса, false для автомобильного
   * @example
   * // Использование в шаблоне
   * <div v-if="isUniversalBusiness">...</div>
   */
  isUniversalBusiness: (state) => {
    return state.isUniversalBusiness
  },

  /**
   * Проверить, нужно ли показывать поля автомобилей
   * @param {Object} state - состояние store
   * @returns {boolean} true если нужно показывать поля автомобилей
   * @example
   * // Использование
   * <div v-if="shouldShowCarFields">...</div>
   */
  shouldShowCarFields: (state) => {
    return state.showCarFields
  },

  /**
   * Проверить, нужно ли показывать поля типов кузова
   * @param {Object} state - состояние store
   * @returns {boolean} true если нужно показывать поля типов кузова
   * @example
   * // Использование
   * <div v-if="shouldShowCarTypeFields">...</div>
   */
  shouldShowCarTypeFields: (state) => {
    return state.showCarTypeFields
  },

  /**
   * Получить сферу деятельности организации
   * @param {Object} state - состояние store
   * @returns {string} Сфера деятельности
   * @example
   * // Например: "Салон красоты", "Автосервис"
   * this.$store.getters['business/getActivityField']
   */
  getActivityField: (state) => {
    return state.activityField
  },

  /**
   * Получить полную информацию об организации
   * @param {Object} state - состояние store
   * @returns {Object|null} Объект с данными организации
   * @example
   * const org = this.$store.getters['business/getOrganization']
   * console.log(org.name, org.phone)
   */
  getOrganization: (state) => {
    return state.organization
  },

  /** Флаг доп. шага онлайн-записи: пост (без графика) или сотрудник (при графике). */
  allowPostSelectionOnline: (state) => {
    return state.organization?.allowPostSelectionOnline === true
  },

  /** Кастомное название шага выбора поста; null → дефолт на фронте. */
  postSelectionStepTitle: (state) => {
    return state.organization?.postSelectionStepTitle ?? null
  },

  scheduleEnabled: (state) => {
    return state.organization?.scheduleEnabled === true
  },

  /** Шаг онлайн-записи выбирает сотрудника (график + доп. шаг включены). */
  onlineSelectsEmployee: (state) => {
    return extraStepSelectsEmployee({
      scheduleEnabled: state.organization?.scheduleEnabled,
      allowPostSelectionOnline: state.organization?.allowPostSelectionOnline,
    })
  },

  allowParallelBooking: (state) => {
    // TEMP: настройка скрыта, всегда выкл.
    return false
    // return state.organization?.allowParallelBooking === true
  },

  /**
   * Получить тип бизнеса
   * @param {Object} state - состояние store
   * @returns {Object|null} Объект с типом бизнеса
   * @example
   * const type = this.$store.getters['business/getBusinessType']
   * console.log(type.name) // "Автомобильный" или "Другое"
   */
  getBusinessType: (state) => {
    return state.businessType
  },

  /**
   * Получить подтип бизнеса
   * @param {Object} state - состояние store
   * @returns {Object|null} Объект с подтипом бизнеса
   * @example
   * const subtype = this.$store.getters['business/getBusinessSubtype']
   * console.log(subtype.name) // "Автосервис", "Шиномонтаж" и т.д.
   */
  getBusinessSubtype: (state) => {
    return state.businessSubtype
  },
}
